import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  IconButton,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";

const columns = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "lastActive", label: "Last Active" },
  { key: "recentAssignment", label: "Recent Activity" },
  { key: "recentScore", label: "Score" },
];

const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const StudentsTable = ({ students = [] }) => {
  const navigate = useNavigate();

  const [sortConfig, setSortConfig] = useState({
    key: "lastName",
    direction: "asc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedStudents = useMemo(() => {
    return [...students].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "lastActive") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [students, sortConfig]);

  const handleRowClick = (student) => {
    if (!student.id) return;
    navigate(`/tutor/students/${student.id}`);
  };

  return (
    <Box
      sx={{
        p: 3,
        mt: 4,
        backgroundColor: "rgba(255,255,255,0.9)",
        borderRadius: 3,
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      }}
    >
      <Typography variant="h6" fontWeight={600} mb={2}>
        Students
      </Typography>

      <Table>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key}>
                <TableSortLabel
                  active={sortConfig.key === col.key}
                  direction={sortConfig.direction}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedStudents.map((s, idx) => (
            <TableRow
              key={idx}
              hover
              onClick={() => handleRowClick(s)}
              sx={{
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              <TableCell>{s.firstName}</TableCell>
              <TableCell>{s.lastName}</TableCell>
              <TableCell>{s.email}</TableCell>
              <TableCell>{formatDate(s.lastActive)}</TableCell>
              <TableCell>{s.recentAssignment || "—"}</TableCell>
              <TableCell>{s.recentScore ?? "—"}</TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default StudentsTable;
